import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { formatAllianceDate } from '../components/alliance/app-banners';
import { SystemSplash } from '../components/alliance/system-screen';
import { MaintenanceScreen } from '../components/alliance/system-screens';
import { Dashboard } from '../components/dashboard';
import { RequireAuth } from '../components/require-auth';
import { RouteContainer } from '../components/route-container';
import { AuthContext } from '../contexts/auth-context';
import { usePermissions } from '../hooks/use-permissions';
import { AgtStaffPermissions, AWS_GROUP } from '../types';
import { captureError } from '../utils/capture-error';
import { maintenanceCheckENV } from '../utils/maintenance-check';
import { Account } from './account';
import { Appointments } from './appointments';
import { Contracts } from './contracts';
import { Documents } from './documents';
import { ErrorsOmissions } from './errors-omissions';
import { Home } from './home';
import { Licenses } from './licenses';
import { MyStaff } from './my-staff';
import { NotFound } from './not-found';
import { Requests } from './requests';
import { SignIn } from './sign-in';
import { Team } from './team';
import { Wallet } from './wallet';
import { WritingNumbers } from './writing-numbers';

const AppRoutes = {
  Admin: React.lazy(() => import('./admin')),
  SignUp: React.lazy(() => import('./sign-up')),
};

/** While the admin chunk downloads: the shell's loading bar, empty page */
function AdminLoading() {
  return (
    <RouteContainer routeTitle="Admin" loading>
      <div />
    </RouteContainer>
  );
}

export function Root() {
  // Hooks
  const { checkPermissions } = usePermissions();
  const {
    state: { session },
  } = React.useContext(AuthContext);

  const isAgtStaff = session?.getAccessToken().decodePayload()['myStaffForAgt'];
  const staffPerms =
    session?.getAccessToken().decodePayload()['cognito:groups'] || [];

  // Query - Check for Maintenance
  const pathMaint = process.env.REACT_APP_MAINT_URL;
  const queryMaint = useQuery({
    enabled: pathMaint !== undefined,
    queryKey: [pathMaint],
    queryFn: async () => {
      if (pathMaint) {
        const body = { data: { origin: window.origin } };
        let response: Response;
        try {
          response = await fetch(pathMaint, {
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
        } catch (error) {
          // The maintenance check is unreachable (offline, blocked): fail open
          // rather than logging "Failed to fetch" on every page load
          return null;
        }
        const data: {
          data: {
            maintenanceActive: boolean;
            maintenanceBegin: string;
            maintenanceEnd: string;
          };
        } = await response.json();

        return data.data;
      }

      return null;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const isAdmin = checkPermissions([
    AWS_GROUP.ReadAll,
    AWS_GROUP.Contracting,
    AWS_GROUP.AgentDataAdmin,
    AWS_GROUP.ImpersonateAll,
    AWS_GROUP.Staff_ReadOnly,
    AWS_GROUP.PermissionsAdmin,
  ]);

  // Agents cannot access the app during Maintenance (RequireAuth shows the
  // maintenance screen). Admins can, with a banner under the App Bar
  const adminMaintenanceEnd =
    isAdmin && queryMaint.data && queryMaint.data.maintenanceActive
      ? queryMaint.data.maintenanceEnd || ''
      : null;

  // Check for maintenance window set by ENV variable
  // Maintenance set by ENV will set the site in maintenance for all users
  const maintDataENV = maintenanceCheckENV();

  if (maintDataENV.maintenanceActive) {
    // Put site in Maintenance Mode for all users
    return (
      <MaintenanceScreen
        until={formatAllianceDate({
          value: maintDataENV.maintenanceEnd,
          long: true,
        })}
      />
    );
  } else {
    return (
      <React.Fragment>
        <Routes>
          <Route path="sign-in" element={<SignIn />} />
          <Route
            path="sign-up/:SignUpSlug"
            element={
              queryMaint.data && queryMaint.data.maintenanceActive ? (
                <Navigate to="/sign-in" replace />
              ) : (
                <React.Suspense fallback={<SystemSplash />}>
                  <AppRoutes.SignUp />
                </React.Suspense>
              )
            }
          />

          <Route
            path="/"
            element={
              <RequireAuth isAdmin={isAdmin} maintData={queryMaint.data}>
                <Dashboard maintenanceEnd={adminMaintenanceEnd} />
              </RequireAuth>
            }
          >
            <Route path="home" element={<Home />} />
            <Route
              path="admin/*"
              element={
                isAdmin ? (
                  <React.Suspense fallback={<AdminLoading />}>
                    <AppRoutes.Admin />
                  </React.Suspense>
                ) : (
                  <Navigate to="/home" replace />
                )
              }
            />
            <Route path="team" element={<Team />} />
            <Route
              path="account"
              element={
                isAgtStaff &&
                !staffPerms.includes(AgtStaffPermissions['Profile']) ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Account />
                )
              }
            />
            <Route
              path="wallet"
              element={
                isAgtStaff &&
                !staffPerms.includes(AgtStaffPermissions['Wallet']) ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Wallet />
                )
              }
            />
            <Route path="licenses" element={<Licenses />} />
            <Route path="errors-omissions" element={<ErrorsOmissions />} />
            <Route path="documents" element={<Documents />} />
            <Route path="requests" element={<Requests />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="writing-numbers" element={<WritingNumbers />} />
            <Route path="appointments" element={<Appointments />} />
            <Route
              path="my-staff"
              element={
                isAgtStaff ? <Navigate to="/home" replace /> : <MyStaff />
              }
            />

            <Route path="/" element={<Navigate to="home" />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </React.Fragment>
    );
  }
}
